import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import LeftRail from "./LeftRail";
import TopBar from "./TopBar";
import SubNav from "./SubNav";
import RouteErrorBoundary from "./RouteErrorBoundary";
import CommandPalette from "../shared/CommandPalette";
import ShortcutsModal from "../shared/ShortcutsModal";
import AssistantFab from "../shared/AssistantFab";
import useKeyboardShortcuts from "../../hooks/useKeyboardShortcuts";
import GuidanceLayer from "../../guidance/GuidanceLayer";
import WhatsAppSetupPrompt from "../onboarding/WhatsAppSetupPrompt";
import OfflineBanner from "../shared/OfflineBanner";

// Mounted once globally. Use `import { toast } from "sonner"` anywhere
// in the app and these settings apply: top-right on desktop, bottom-center
// on mobile, VF-themed, dismissible by Esc / click, durations are tuned
// per type (4s success/info, 8s warning, sticky for errors).
function AppToaster() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return (
    <Toaster
      position={isMobile ? "bottom-center" : "top-right"}
      closeButton
      richColors
      toastOptions={{
        duration: 4000,
        classNames: {
          toast: "!font-sans !text-sm",
          title: "!text-vf-base !font-semibold",
          description: "!text-vf-muted",
          actionButton: "!bg-vf-primary !text-white",
          cancelButton: "!bg-vf-tint !text-vf-base",
        },
      }}
    />
  );
}

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Cmd+K / Ctrl+K — keyboard shortcuts hook can't catch these because it
  // ignores meta/ctrl by design (so OS shortcuts win). Wire this one directly.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    // TopBar's search button dispatches this so we don't have to prop-drill.
    const onOpen = () => setPaletteOpen(true);
    window.addEventListener("amplehire:open-command-palette", onOpen);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("amplehire:open-command-palette", onOpen);
    };
  }, []);

  // Single-key + chord shortcuts. Skipped automatically in input fields.
  const go = useCallback((to) => () => navigate(to), [navigate]);
  useKeyboardShortcuts({
    "?":    () => setShortcutsOpen(true),
    "g d":  go("/admin/dashboard-v2"),
    "g j":  go("/admin/jobs"),
    "g i":  go("/admin/interviews"),
    "g c":  go("/admin/compare"),
    "g p":  go("/admin/pipeline"),
    "g s":  go("/admin/source-hub"),
    "g t":  go("/admin/talent-pools"),
    "g a":  go("/admin/analytics"),
    "n j":  go("/admin/create-job"),
    "n c":  go("/admin/invite-candidates"),
  });

  return (
    <div className="min-h-screen flex bg-vf-bg">
      <OfflineBanner />
      {/* WCAG 2.4.1 — bypass blocks. First focusable element so keyboard
          users can skip the sidebar + nav and land on page content. */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <LeftRail />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar />
        <SubNav />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto focus:outline-none"
        >
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
            <RouteErrorBoundary locationKey={location.key}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>
      </div>
      <AppToaster />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onOpenShortcuts={() => setShortcutsOpen(true)}
      />
      <ShortcutsModal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
      <AssistantFab />
      <GuidanceLayer />
      <WhatsAppSetupPrompt />
    </div>
  );
}
